import { useState, useEffect, useCallback } from "react";
import {
  X, Pill, Car, HeartHandshake, Stethoscope, Clock,
  CheckCircle2, XCircle, Loader2, Share2, RotateCcw, UtensilsCrossed, ShoppingBasket, Brush, Wrench, UtensilsCrossed as DineoutIcon,
  Trash2, EyeOff, Eye,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = {
  medicine: Pill,
  cab: Car,
  nurse: HeartHandshake,
  doctor: Stethoscope,
  food: UtensilsCrossed,
  grocery: ShoppingBasket,
  dineout: DineoutIcon,
  ride: Car,
  maid: Brush,
  handyman: Wrench,
};

const SERVICE_LABELS = {
  medicine: "Medicine Order",
  cab: "Cab Booking",
  nurse: "Nurse Request",
  doctor: "Doctor Appointment",
  food: "Food Order",
  grocery: "Grocery Order",
  dineout: "Restaurant Booking",
  ride: "Ride Booking",
  maid: "Maid Booking",
  handyman: "Plumber/Carpenter",
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  accepted: { label: "Accepted", color: "bg-blue-100 text-blue-800", icon: CheckCircle2 },
  in_progress: { label: "In Progress", color: "bg-purple-100 text-purple-800", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle },
};

function describeItem(item) {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const parts = [];
    if (item.quantity) parts.push(String(item.quantity));
    const name = item.name || item.item || item.dish || item.product;
    if (name) parts.push(name);
    if (item.brand && item.brand !== "any brand is fine") parts.push(`(${item.brand})`);
    return parts.join(" ") || "Item";
  }
  return String(item);
}

function formatDate(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function OrdersPanel({ orders, onClose, sessionId, onMessageUpdate, onReorder, onOrdersChanged }) {
  const [familyContacts, setFamilyContacts] = useState([]);
  const [sharingOrderId, setSharingOrderId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [hiddenIds, setHiddenIds] = useState(() => new Set(orders.filter((o) => o.hidden_from_family).map((o) => o.id)));
  const [hidingId, setHidingId] = useState(null);

  useEffect(() => {
    setHiddenIds(new Set(orders.filter((o) => o.hidden_from_family).map((o) => o.id)));
  }, [orders]);

  const loadFamilyContacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/family/${sessionId}`);
      setFamilyContacts(res.data.contacts || []);
    } catch (e) {
      console.error("Failed to load family contacts:", e);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      loadFamilyContacts();
    }
  }, [sessionId, loadFamilyContacts]);

  const shareWithFamily = async (orderId, contactId, contactName) => {
    try {
      const res = await axios.post(`${API}/family/share-order`, {
        order_id: orderId,
        family_contact_id: contactId,
      });
      toast.success(`Shared with ${contactName}`);
      setSharingOrderId(null);
      if (onMessageUpdate) onMessageUpdate(res.data.notification);
    } catch (e) {
      toast.error("Could not share order. Please try again.");
    }
  };

  const handleReorder = async (orderId) => {
    setReorderingId(orderId);
    try {
      const res = await axios.post(`${API}/orders/reorder`, {
        order_id: orderId,
        session_id: sessionId,
      });
      toast.success("Reorder placed successfully!");
      if (onMessageUpdate) onMessageUpdate(res.data.notification);
      if (onReorder) onReorder(res.data.order);
    } catch (e) {
      toast.error("Could not reorder. Please try again.");
    } finally {
      setReorderingId(null);
    }
  };

  const handleDelete = async (orderId) => {
    setDeletingId(orderId);
    try {
      await axios.delete(`${API}/orders/${orderId}`, { params: { session_id: sessionId } });
      toast.success("Order deleted");
      setConfirmDeleteId(null);
      if (onOrdersChanged) onOrdersChanged();
    } catch (e) {
      toast.error("Could not delete this order. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleHideFromFamily = async (orderId, nextHidden) => {
    setHidingId(orderId);
    try {
      await axios.patch(`${API}/orders/${orderId}/visibility`, {
        session_id: sessionId,
        hidden_from_family: nextHidden,
      });
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (nextHidden) next.add(orderId);
        else next.delete(orderId);
        return next;
      });
      toast.success(nextHidden ? "Hidden from your caregivers" : "Visible to your caregivers again");
    } catch (e) {
      toast.error("Could not update this order. Please try again.");
    } finally {
      setHidingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="orders-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4 shrink-0"
          style={{ backgroundColor: "#2D2766" }}
        >
          <h2 className="text-white font-semibold text-lg">Your Requests</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="close-orders-btn"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Orders list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
              <Clock className="w-12 h-12" />
              <p className="text-lg">No requests yet</p>
              <p className="text-sm text-center">
                Your medicine orders, cab bookings, and other requests will appear here.
              </p>
            </div>
          ) : (
            orders.map((order) => {
              const Icon = SERVICE_ICONS[order.service_type] || Pill;
              const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConf.icon;
              const isSharing = sharingOrderId === order.id;

              return (
                <div
                  key={order.id}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                  data-testid={`order-${order.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-900">
                        {SERVICE_LABELS[order.service_type] || order.service_type}
                      </span>
                    </div>
                    <Badge
                      className={`${statusConf.color} border-0 flex items-center gap-1 text-xs`}
                    >
                      <StatusIcon className="w-3 h-3" />
                      {statusConf.label}
                    </Badge>
                  </div>

                  {/* Details */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-2">
                    {Object.entries(order.details || {}).map(([key, value]) => {
                      // Render structured medicines list
                      if (key === "medicines" && Array.isArray(value)) {
                        return (
                          <div key={key} className="space-y-1">
                            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Medicines:</span>
                            {value.map((med, i) => (
                              <div key={`${key}-${i}-${typeof med === "string" ? med : JSON.stringify(med)}`} className="flex items-center gap-2 pl-1 py-0.5">
                                <Pill className="w-3 h-3 text-emerald-500 shrink-0" />
                                <span className="text-sm text-gray-800">
                                  {typeof med === "string" ? (
                                    med
                                  ) : (
                                    <>
                                      <span className="font-medium">{med.name || med.medicine_name || "Unknown"}</span>
                                      {(med.dosage || med.strength) && <span className="text-gray-500"> {med.dosage || med.strength}</span>}
                                      {med.quantity && <span className="text-gray-500"> — {med.quantity}</span>}
                                    </>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      // Render medicine_name fields from older order format
                      if ((key === "medicine_name" || key === "medicine") && typeof value === "string") {
                        return (
                          <div key={key} className="flex items-center gap-2 py-0.5">
                            <Pill className="w-3 h-3 text-emerald-500 shrink-0" />
                            <span className="text-sm text-gray-800 font-medium">{value}</span>
                          </div>
                        );
                      }
                      // Structured "items" list (grocery/food) — show as bullets, not [object Object]
                      if (key === "items" && Array.isArray(value)) {
                        return (
                          <div key={key} className="space-y-1">
                            <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Items:</span>
                            {value.map((it, i) => (
                              <div key={`${key}-${i}`} className="flex items-center gap-2 pl-1 py-0.5">
                                <ShoppingBasket className="w-3 h-3 text-emerald-500 shrink-0" />
                                <span className="text-sm text-gray-800">{describeItem(it)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      // Other arrays — describe each entry instead of joining raw objects
                      if (Array.isArray(value)) {
                        return (
                          <div key={key} className="text-sm py-0.5">
                            <span className="text-gray-500 capitalize">{key.replace(/_/g, " ")}:</span>
                            <span className="text-gray-800 ml-1">{value.map(describeItem).join(", ")}</span>
                          </div>
                        );
                      }
                      // Skip empty/null values (e.g. "preferences: null") — nothing useful to show
                      if (value === null || value === undefined || value === "") return null;
                      if (typeof value === "object") return null;
                      return (
                        <div key={key} className="flex justify-between text-sm py-0.5">
                          <span className="text-gray-500 capitalize">
                            {key.replace(/_/g, " ")}:
                          </span>
                          <span className="text-gray-800 font-medium text-right max-w-[60%]">
                            {String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Vendor notes */}
                  {order.vendor_notes && (
                    <div className="bg-blue-50 rounded-lg p-2 mb-2 text-sm text-blue-800">
                      Provider: {order.vendor_notes}
                    </div>
                  )}

                  {/* Share with family */}
                  <div className="mb-2">
                    {familyContacts.length > 0 ? (
                      isSharing ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid={`share-picker-${order.id}`}>
                          <p className="text-sm font-medium text-green-800 mb-2">Share with:</p>
                          <div className="space-y-1.5">
                            {familyContacts.map((contact) => (
                              <button
                                key={contact.id}
                                onClick={() => shareWithFamily(order.id, contact.id, contact.name)}
                                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-green-100 transition-colors text-left"
                                data-testid={`share-with-${contact.id}`}
                              >
                                <div className="w-8 h-8 rounded-full bg-[#3D3580] flex items-center justify-center text-white text-sm font-bold">
                                  {contact.name.charAt(0)}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{contact.name}</p>
                                  <p className="text-xs text-gray-500">{contact.relation} - {contact.phone}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => setSharingOrderId(null)}
                            className="w-full text-sm text-gray-500 mt-2 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSharingOrderId(order.id)}
                          className="flex items-center gap-2 text-sm text-[#2D2766] hover:text-[#3D3580] transition-colors font-medium"
                          data-testid={`share-order-${order.id}`}
                        >
                          <Share2 className="w-4 h-4" />
                          Share with Family
                        </button>
                      )
                    ) : (
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Share2 className="w-3 h-3" />
                        Add family contacts to share orders
                      </p>
                    )}
                  </div>

                  {/* Reorder button */}
                  <button
                    onClick={() => handleReorder(order.id)}
                    disabled={reorderingId === order.id}
                    className="flex items-center gap-2 text-sm font-medium text-[#3D3580] hover:text-[#2D2766] transition-colors mb-2 disabled:opacity-50"
                    data-testid={`reorder-${order.id}`}
                  >
                    <RotateCcw className={`w-4 h-4 ${reorderingId === order.id ? "animate-spin" : ""}`} />
                    {reorderingId === order.id ? "Placing reorder..." : "Reorder"}
                  </button>

                  {/* Hide from caregivers / Delete */}
                  <div className="flex items-center gap-4 mb-2">
                    <button
                      onClick={() => handleToggleHideFromFamily(order.id, !hiddenIds.has(order.id))}
                      disabled={hidingId === order.id}
                      className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                      data-testid={`toggle-hide-family-${order.id}`}
                    >
                      {hiddenIds.has(order.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {hiddenIds.has(order.id) ? "Hidden from caregivers" : "Hide from caregivers"}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(order.id)}
                      className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
                      data-testid={`delete-order-${order.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>

                  {confirmDeleteId === order.id && (
                    <div
                      className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 flex items-center justify-between gap-3"
                      data-testid={`confirm-delete-${order.id}`}
                    >
                      <p className="text-sm text-red-800">Delete this order permanently?</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => handleDelete(order.id)}
                          disabled={deletingId === order.id}
                          className="text-sm font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                          data-testid={`confirm-delete-yes-${order.id}`}
                        >
                          {deletingId === order.id ? "Deleting…" : "Yes, delete"}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                          data-testid={`confirm-delete-cancel-${order.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-400">
                    {formatDate(order.created_at)}
                    {order.reordered_from && (
                      <span className="ml-2 text-emerald-500 font-medium">Reorder</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
